import { Box } from '@trmediaab/zebra';
import { Fragment } from 'react';
import { Element, Link } from 'react-scroll';

import { PageTitle, Table, VerticalList } from '~/components/Elements';
import TwoColumnLayout from '~/components/TwoColumnLayout';
import StatisticsFetcher from '~/containers/Statistics';

import Favourite from './Favourite';
import FormStable from './FormStable';
import { FrontDrivers, TrackFrontRunners } from './Frontrunner';
import RoundInfo from './RoundInfo';
import {
  Disclaimer,
  FactSpecial,
  Headline,
  Intro,
  Label,
  Menu,
  TableSideValue,
} from './styles';
import TrackInfo from './TrackInfo';
import Tracks from './Tracks';
import Venue from './Venue';

const ScrollMenu = ({ round }) => {
  const tracks = round.meets.map(meet => meet.track_name).join('/');
  return (
    <Menu>
      <Label>Statistik</Label>
      <VerticalList>
        {round.meets.map(meet => (
          <ScrollLink
            label={meet.track_name}
            to={`track-${meet.track_id}`}
            key={meet.track_id}
          />
        ))}

        <ScrollLink
          label={`${round.bet_type}-statistik ${tracks}`}
          to="betinfo"
        />

        <ScrollLink label={`Spetsstatistik ${tracks}`} to="front_track" />
        <ScrollLink label="Bästa favoritkuskarna" to="favorite_drivers" />
        <ScrollLink label="Bästa skrällkuskarna" to="sensation_drivers" />
        <ScrollLink label="Bästa favorittränarna" to="favorite_trainers" />
        <ScrollLink label="Spårstatistik" to="tracks" />
        <ScrollLink label="Spårstatistik favoriter" to="tracks_favourites" />
        <ScrollLink label="Spetshästar" to="tracks_frontrunner" />
        <ScrollLink label="Spetskuskar" to="front_drivers" />
        <ScrollLink label="Skrällar" to="sensations" />
        <ScrollLink label="Favoriter" to="favourites" />
        <ScrollLink label="Formkuskar" to="formstable" />
        <ScrollLink label="Formstall" to="formstable_trainers" />
      </VerticalList>
    </Menu>
  );
};

const ScrollLink = ({ label, to }) => (
  <li>
    <Link activeClass="active" to={to} spy smooth offset={-180} duration={200}>
      {label}
    </Link>
  </li>
);

const FactTable = ({ values }) => (
  <Fragment>
    <Headline>{values.headline}</Headline>

    <Intro>{values.intro}</Intro>

    <Table>
      <thead>
        <tr>
          <th>Antal</th>
          <th>Träffprocent</th>
        </tr>
      </thead>
      <tbody>
        {values.result &&
          values.result
            .filter(val => val.val > 0 || val.total > 0)
            .map(val => (
              <tr key={val.n}>
                <td>{val.n}</td>
                <td>
                  {val.val}%
                  {val.total && <TableSideValue>{val.total}%</TableSideValue>}
                </td>
              </tr>
            ))}
      </tbody>
    </Table>

    <Disclaimer>
      Siffror inom parentes anger medelvärden för Sveriges samtliga travbanor.
    </Disclaimer>
  </Fragment>
);

const Statistics = ({ round }) => (
  <StatisticsFetcher
    id={round.id}
    type="round"
    render={stats => (
      <Box bg="white" px={{ _: 3, md: '3,5' }} py={{ _: 3, md: '3,5' }}>
        <TwoColumnLayout aside={<ScrollMenu round={round} />}>
          <PageTitle>Statistik</PageTitle>

          <Disclaimer>
            OBS: statistiken är enbart grundad på starter gjorda i Sverige.
          </Disclaimer>

          {stats.round.tracks.map(track => (
            <Element name={`track-${track.id}`} key={track.id}>
              <TrackInfo avg={stats.round.track_median} track={track} />
            </Element>
          ))}

          <Element name="betinfo">
            <RoundInfo round={round} stats={stats.round} />

            {stats.special &&
              Object.keys(stats.special).map(key => (
                <Fragment key={key}>
                  <FactSpecial stats={stats.special[key]} />
                </Fragment>
              ))}
          </Element>

          <Element name="front_track">
            <Venue round={round} data={stats} />
          </Element>

          <Element name="favorite_drivers">
            <Favourite round={round} data={stats} type="favorite_drivers" />
          </Element>

          <Element name="sensation_drivers">
            <Favourite round={round} data={stats} type="sensation_drivers" />
          </Element>

          <Element name="favorite_trainers">
            <Favourite round={round} data={stats} type="favorite_trainers" />
          </Element>

          <Element name="tracks">
            <Tracks round={round} data={stats} type="tracks" />
          </Element>

          <Element name="tracks_favourites">
            <Tracks round={round} data={stats} type="tracks_favourites" />
          </Element>

          <Element name="tracks_frontrunner">
            <TrackFrontRunners
              round={round}
              data={stats}
              type="tracks_frontrunner"
            />
          </Element>

          <Element name="front_drivers">
            <FrontDrivers round={round} data={stats} type="front_drivers" />
          </Element>

          {stats.round.nr_sensation_wins && (
            <Element name="sensations">
              <FactTable values={stats.round.nr_sensation_wins} />
            </Element>
          )}

          {stats.round.nr_favourite_wins && (
            <Element name="favourites">
              <FactTable values={stats.round.nr_favourite_wins} />
            </Element>
          )}

          <Element name="formstable">
            <FormStable round={round} data={stats} type="formstable_drivers" />
          </Element>

          <Element name="formstable_trainers">
            <FormStable round={round} data={stats} type="formstable_trainers" />
          </Element>
        </TwoColumnLayout>
      </Box>
    )}
  />
);

export default Statistics;
